/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';

import { MODE_NONE, MODE_GROUPS, MODE_CUSTOM } from '../../../helpers/schedule-tag-format';

/**
 * The choice between no schedule, a preset, and days and times chosen by hand.
 *
 * Presentational only: the selected mode arrives as a prop and every change is reported through
 * `onChange`. The Schedule_Modal owns the observable state, so this component holds none and does
 * no data fetching.
 *
 * Props:
 * - mode: 'none' | 'groups' | 'custom'
 * - onChange: (mode) => void
 * - disabled: boolean
 */

// The mode values come from the grammar helper rather than being spelled here, so that the modal,
// the helper and this toggle cannot drift apart.
//
// The labels carry no descriptions. Each one says what choosing it does, and a sentence under each
// restating that in longer words is three sentences the user reads before reaching the control. What
// Off means for the workspace is in the disclosure with the rest of the scheduler's behaviour.
const MODE_OPTIONS = [
  { value: MODE_NONE, label: 'Off' },
  { value: MODE_GROUPS, label: 'Use a preset' },
  { value: MODE_CUSTOM, label: 'Choose days and times' },
];

// Space and Enter both activate a radio in practice, so both are honoured. Arrow keys are left to
// the browser's default Tab order rather than implementing roving focus, matching the keyboard
// handling of the other custom controls in this directory.
const ACTIVATION_KEYS = [' ', 'Spacebar', 'Enter'];

class WorkspaceScheduleModeToggle extends React.Component {
  handleSelect = value => () => {
    if (this.props.disabled) return;
    if (value === this.props.mode) return;
    const onChange = this.props.onChange;
    if (onChange) onChange(value);
  };

  handleKeyDown = value => event => {
    if (!ACTIVATION_KEYS.includes(event.key)) return;
    // Space would otherwise scroll the modal body.
    event.preventDefault();
    this.handleSelect(value)();
  };

  render() {
    const mode = this.props.mode;
    const disabled = !!this.props.disabled;

    return (
      <div className="wsch-modal-mode-toggle" role="radiogroup" aria-label="Schedule mode">
        {MODE_OPTIONS.map(option => {
          const selected = mode === option.value;
          const classNames = ['wsch-modal-mode-option'];
          if (selected) classNames.push('selected');
          // The option is not a native control, so it takes the shared disabled treatment by class
          // rather than through :disabled.
          if (disabled) classNames.push('wsch-modal-disabled');

          return (
            <div
              key={option.value}
              className={classNames.join(' ')}
              role="radio"
              aria-checked={selected}
              aria-disabled={disabled}
              tabIndex={disabled ? -1 : 0}
              onClick={this.handleSelect(option.value)}
              onKeyDown={this.handleKeyDown(option.value)}
              data-testid={`schedule-mode-${option.value}`}
            >
              <span className={`wsch-modal-radio${selected ? ' checked' : ''}`} aria-hidden="true" />
              <span className="wsch-modal-mode-option-label">{option.label}</span>
            </div>
          );
        })}
      </div>
    );
  }
}

export default WorkspaceScheduleModeToggle;
export { MODE_OPTIONS };
